import { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";

import { AnimatedPage, AnimatedCard, StaggerContainer, StaggerItem, AnimatedCounter, ParticleField, SectionHeader } from "../BizDevAnimations";
import "../BizDevAnimations.css";
import { useCanViewFinancials } from "../hooks/useCanViewFinancials";

const STAGE_COLORS = { Cold: "#64748B", Warm: "#F59E0B", Hot: "#EF4444", Qualified: "#8B5CF6", "RFP Received": "#06B6D4", "Proposal Sent": "#3B82F6", "Follow-up": "#F97316", Negotiation: "#EC4899", Won: "#22C55E", Lost: "#6B7280" };
const STAGE_BG = Object.fromEntries(Object.entries(STAGE_COLORS).map(([k, v]) => [k, v + "18"]));
const fmt = (lakhs) => { if (!lakhs && lakhs !== 0) return "₹0"; if (Math.abs(lakhs) >= 100) return `₹${(lakhs / 100).toFixed(1)}Cr`; return `₹${lakhs.toFixed(1)}L`; };
const fmtL = (v) => v ? `₹${Number(v).toFixed(1)}L` : "₹0";
const timeAgo = (d) => { if (!d) return ""; const ms = Date.now() - new Date(d).getTime(); const m = Math.floor(ms / 60000); if (m < 60) return `${m}m ago`; const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`; return `${Math.floor(h / 24)}d ago`; };
const daysUntil = (d) => { if (!d) return null; return Math.ceil((new Date(d) - new Date()) / 86400000); };

function Chip({ label, value, color, sub }) {
  return (
    <AnimatedPage><div style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 10, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent)`, opacity: 0.5 }} />
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', letterSpacing: '.12em', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-orb)', fontSize: 22, fontWeight: 700, color: 'var(--t1)' }}>{value}</div>
      {sub && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', marginTop: 4 }}>{sub}</div>}
    </div></AnimatedPage>
  );
}

export default function BizDevDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const canViewFinancials = useCanViewFinancials();
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, r, f, l] = await Promise.all([
        api.get("/ceo/dashboard").catch(() => ({ data: null })),
        api.get("/bizdev/dashboard/revenue").catch(() => ({ data: null })),
        api.get("/funnel/stats").catch(() => ({ data: null })),
        api.get("/leads").catch(() => ({ data: [] })),
      ]);
      setStats(s.data); setRevenue(r.data); setFunnel(f.data); setLeads(Array.isArray(l.data) ? l.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', fontFamily: 'var(--font-orb)', fontSize: 14, color: 'var(--t3)' }}>⚡ Loading...</div>;

  const rev = stats?.revenue || {};
  const biz = stats?.bizdev || {};
  const hr = stats?.hr || {};
  const achievedLakhs = (rev.total_cr || 0) * 100;
  const targetLakhs = 5000;
  const pct = targetLakhs > 0 ? Math.min((achievedLakhs / targetLakhs) * 100, 100) : 0;
  const hotLeads = leads.filter(l => ["Hot", "Negotiation", "Proposal Sent"].includes(l.funnel_stage));
  const ins = funnel?.insights || {};
  const stages = funnel?.stages || [];
  const today = new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

  return (
    <AnimatedPage><div className="dm-page-container" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-orb)', fontSize: 20, fontWeight: 700, color: 'var(--t1)', letterSpacing: '.06em' }}>BIZ DEV <span style={{ color: 'var(--r)' }}>COMMAND</span></h1>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', letterSpacing: '.14em', marginTop: 4 }}>{today.toUpperCase()}</div>
        </div>
        <button onClick={load} style={{ fontFamily: 'var(--font-mono)', fontSize: 8, padding: '6px 14px', background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 6, color: 'var(--t2)', cursor: 'pointer', letterSpacing: '.1em' }}>↻ REFRESH</button>
      </div>

      {/* Revenue Pulse */}
      <div style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <span style={{ fontFamily: 'var(--font-orb)', fontSize: 32, fontWeight: 700, color: 'var(--t1)' }}>₹{(rev.total_cr || 0).toFixed(1)}Cr</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', marginLeft: 10 }}>of ₹50Cr annual target</span>
          </div>
          <span style={{ fontFamily: 'var(--font-orb)', fontSize: 16, color: pct >= 50 ? 'var(--g)' : pct >= 25 ? 'var(--a)' : 'var(--r)' }}>{pct.toFixed(1)}%</span>
        </div>
        <div style={{ height: 6, background: 'var(--s3)', borderRadius: 3, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, var(--r), var(--a), var(--g))`, transition: 'width 1s ease-out' }} />
        </div>
      </div>

      {/* KPI Grid */}
        {isAdmin && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 16 }} className="dm-kpi-grid grid-stagger">
        <Chip label="PIPELINE" value={leads.length} color="var(--c)" sub="total leads" />
        <Chip label="RETAINERS" value={biz.retainer_clients || 0} color="var(--g)" sub="active accounts" />
        <Chip label="WIN RATE" value={`${ins.probability_percent || 0}%`} color="var(--a)" sub="hit probability" />
        <Chip label="AVG DEAL" value={fmt(leads.length > 0 ? leads.reduce((s, l) => s + (l.cy_revenue_lakhs || 0), 0) / Math.max(leads.filter(l => l.cy_revenue_lakhs > 0).length, 1) : 0)} color="var(--p)" />
        <Chip label="BRIEFS" value={biz.active_briefs || 0} color="var(--a)" sub="active" />
        <Chip label="HOT LEADS" value={hotLeads.length} color="var(--r)" sub="follow-up needed" />
      </div>
        )}

      {/* Funnel */}
      {stages.length > 0 && (
        <div style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 18, marginBottom: 16 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.1em', marginBottom: 14 }}>SALES FUNNEL</div>
          {stages.filter(s => !["Won", "Lost"].includes(s.stage)).map(s => {
            const maxCount = Math.max(...stages.map(x => x.count), 1);
            return (
              <div key={s.stage} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: STAGE_COLORS[s.stage] || '#666', flexShrink: 0 }} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', width: 90 }}>{s.stage}</span>
                <span style={{ fontFamily: 'var(--font-orb)', fontSize: 11, color: 'var(--t1)', width: 28, textAlign: 'right' }}>{s.count}</span>
                <div style={{ flex: 1, height: 5, background: 'var(--s3)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(s.count / maxCount) * 100}%`, background: STAGE_COLORS[s.stage] || '#666', borderRadius: 2, transition: 'width .6s' }} />
                </div>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)', width: 50, textAlign: 'right' }}>{fmt(s.total_lakhs)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Bottom 2-col */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="dm-bottom-grid">
        <div>
          {/* Upcoming Pitches */}
          <div className="kpi-scan anim-card" style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 16, position: "relative", overflow: "hidden", marginBottom: 12, position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.1em', marginBottom: 10 }}>UPCOMING PITCHES</div>
            {(stats?.upcoming_pitches || []).length === 0 ? <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', textAlign: 'center', padding: 20 }}>No upcoming pitches</div> :
              (stats.upcoming_pitches || []).slice(0, 5).map(b => (
                <div key={b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)} style={{ padding: '8px 0', borderBottom: '1px solid var(--bdr)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div><div style={{ fontSize: 12, color: 'var(--t1)' }}>{b.title}</div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)' }}>{b.company_name} · {b.pitch_date}</div></div>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 7, padding: '2px 6px', borderRadius: 3, background: b.priority === "Critical" ? 'var(--rd)' : b.priority === "High" ? 'var(--ad)' : 'var(--s3)', color: b.priority === "Critical" ? 'var(--r)' : b.priority === "High" ? 'var(--a)' : 'var(--t3)' }}>{b.priority}</span>
                </div>
              ))}
          </div>
          {/* Hot Leads */}
          <div className="kpi-scan anim-card" style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.1em', marginBottom: 10 }}>HOT LEADS</div>
            {hotLeads.slice(0, 5).map(l => (
              <div key={l.lead_id} onClick={() => navigate(`/bizdev/leads/${l.lead_id}`)} style={{ padding: '8px 0', borderBottom: '1px solid var(--bdr)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                <div><div style={{ fontSize: 12, color: 'var(--t1)' }}>{l.company_name}</div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)' }}>{l.funnel_stage} · Score: {l.ai_score || "—"}</div></div>
                {l.cy_revenue_lakhs > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--g)' }}>{canViewFinancials ? fmt(l.cy_revenue_lakhs) : "---"}</span>}
              </div>
            ))}
          </div>
        </div>
        <div>
          {/* Recently Won */}
          <div className="kpi-scan anim-card" style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 16, position: "relative", overflow: "hidden", marginBottom: 12, position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.1em', marginBottom: 10 }} className="shimmer-text">RECENTLY WON</div>
            {(stats?.recently_won || []).length === 0 ? <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t3)', textAlign: 'center', padding: 20 }}>No won briefs yet</div> :
              (stats.recently_won || []).map(b => (
                <div key={b.brief_id} style={{ padding: '8px 0', borderBottom: '1px solid var(--bdr)', display: 'flex', justifyContent: 'space-between' }}>
                  <div><div style={{ fontSize: 12, color: 'var(--t1)' }}>{b.title}</div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 8, color: 'var(--t3)' }}>{b.company_name}</div></div>
                  {b.revenue_generated_lakhs && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--g)' }}>{canViewFinancials ? fmt(b.revenue_generated_lakhs) : "---"}</span>}
                </div>
              ))}
          </div>
          {/* Activity */}
          <div className="kpi-scan anim-card" style={{ background: 'var(--s1)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 16, position: "relative", overflow: "hidden" }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--t2)', letterSpacing: '.1em', marginBottom: 10 }} className="shimmer-text">ACTIVITY</div>
            {(stats?.activity_feed || []).slice(0, 6).map((a, i) => (
              <div key={a.event_id || i} style={{ padding: '6px 0', borderBottom: '1px solid var(--bdr)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: a.module === 'hr' ? 'var(--p)' : 'var(--c)', marginTop: 5, flexShrink: 0 }} />
                <div><div style={{ fontSize: 11, color: 'var(--t2)' }}>{a.summary}</div><div style={{ fontFamily: 'var(--font-mono)', fontSize: 7, color: 'var(--t3)' }}>{a.actor_name} · {timeAgo(a.created_at)}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

        {isAdmin && (
      <style>{`@media(max-width:768px){.dm-kpi-grid{grid-template-columns:repeat(2,1fr)!important}.dm-bottom-grid{grid-template-columns:1fr!important}}`}</style>
    )}
    </div></AnimatedPage>
  );
}
